/**
 * Stable identity for AST nodes.
 *
 * The naive scheme — index the node by its position in the tree (`0/if/then/2`)
 * — is unstable in exactly the way that hurts most: typing a new line at the
 * top of the document renumbers everything below it, the graph compiler emits
 * all-new node ids, and React tears down and rebuilds the entire diagram on a
 * single keystroke.
 *
 * Instead an id is derived from *what the node is* — its kind plus its label —
 * disambiguated by how many identical (kind, label) pairs came before it in
 * document order. Inserting an unrelated statement leaves every other node's id
 * untouched; renaming a label changes only that node.
 */

/**
 * FNV-1a, 32-bit. Small, fast, no dependencies, and good enough here: a
 * collision only means two nodes briefly share a React key within one render,
 * and the occurrence counter already separates genuinely identical nodes.
 */
function hash32(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function encode(value: number): string {
  return value.toString(36).padStart(7, '0');
}

/**
 * Hands out ids for one parse. Create a fresh instance per document — the
 * occurrence counters must restart so the same source always yields the same
 * ids (determinism is relied on by the tests and by diagram stability).
 */
export class StableIdFactory {
  private readonly counters = new Map<string, number>();

  /**
   * @param kind    node kind, e.g. `ActionStatement`
   * @param content label text or another distinguishing string; may be empty
   */
  next(kind: string, content = ''): string {
    const key = `${kind} ${content}`;
    const occurrence = this.counters.get(key) ?? 0;
    this.counters.set(key, occurrence + 1);
    const suffix = occurrence === 0 ? '' : `_${occurrence}`;
    return `${shortKind(kind)}_${encode(hash32(key))}${suffix}`;
  }

  reset(): void {
    this.counters.clear();
  }
}

/** Compact, readable prefix so ids stay debuggable in the DOM inspector. */
function shortKind(kind: string): string {
  switch (kind) {
    case 'Program':
      return 'prog';
    case 'Block':
      return 'blk';
    case 'FunctionDeclaration':
      return 'fn';
    case 'ActionStatement':
      return 'act';
    case 'StartStatement':
      return 'start';
    case 'EndStatement':
      return 'end';
    case 'IfStatement':
      return 'if';
    case 'WhileStatement':
      return 'while';
    case 'RepeatStatement':
      return 'rep';
    case 'ForEachStatement':
      return 'each';
    case 'SwitchStatement':
      return 'sw';
    case 'SwitchCase':
      return 'case';
    case 'CallStatement':
      return 'call';
    case 'ReturnStatement':
      return 'ret';
    case 'BreakStatement':
      return 'brk';
    case 'ContinueStatement':
      return 'cont';
    case 'ErrorStatement':
      return 'err';
    default:
      return 'n';
  }
}

/**
 * Derives a child id from a parent id. Used by the graph compiler when one AST
 * node expands into several graph nodes (a loop becomes a decision plus a
 * junction, a call in expanded mode becomes an entry and an exit, and so on).
 */
export function deriveId(parentId: string, role: string, index = 0): string {
  return index === 0 ? `${parentId}:${role}` : `${parentId}:${role}${index}`;
}
